<template>
  <div :class="$style.container">
    <div :class="$style.search">
      <Icon :class="$style.icon" name="search" size="medium" />
      <input
        ref="inputFieldRef"
        v-model="inputField"
        aria-label="searchphrase"
        :class="$style.input"
        maxlength="50"
        :placeholder="searchPlaceholder"
        type="text"
        @input.prevent="handleChange"
        @keyup.delete="handleReset"
        @keyup.down="handleKeyUpDown"
        @keyup.enter="handleKeyUpEnter"
        @keyup.up="handleKeyUpUp"
      />
      <Icon
        v-if="inputField"
        :class="$style.reset"
        name="close"
        size="medium"
        @click="
          () => {
            inputField = '';
            handleChange();
            handleReset();
          }
        "
      />
    </div>
    <div :class="$style.label" @click.prevent="handleSubmit">
      <input aria-label="submit search phrase" :title="searchPlaceholder" type="submit" value="" />
    </div>
    <div v-if="didYouMean" :class="$style.didYouMean">
      {{ storeTranslations.translations?.['didYouMean'] }}
      <Button1XP
        :class="$style.didYouMeanPhrase"
        size="xs"
        :text="didYouMean"
        variant="link"
        @click="handleDidYouMeanClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import Icon from '../Icon/Icon.vue';
import { useStoreTranslations } from '../../store/translations/translations.ts';

const props = defineProps({
  searchPlaceholder: {
    type: String,
  },
  selectedSuggestion: {
    type: String,
  },
  model: {
    type: Array,
    default: () => [],
  },
  showDidYouMean: {
    type: Boolean,
    default: false,
  },
  searchSynonymSuggest: {
    type: Array,
    default: () => [],
  },
  searchSynonym: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  'active',
  'change',
  'inactive',
  'initialize',
  'keyUpDown',
  'keyUpEnter',
  'keyUpUp',
  'reset',
  'submitted',
]);

const storeTranslations = useStoreTranslations();

const inputField = ref('');

const didYouMean = computed(() => {
  if (!props.showDidYouMean) {
    return false;
  } else if (props.searchSynonym) {
    return props.searchSynonym;
  } else if (props.searchSynonymSuggest.length > 0) {
    return props.searchSynonymSuggest.join(' ');
  }
  return false;
});

const inputFieldRef = ref(null);

const handleDidYouMeanClick = () => {
  inputField.value = didYouMean;
  inputFieldRef.value.focus();
  handleSubmit();
};

const handleSubmit = () => {
  getSearchResults(prepareTerms());
  emit('submitted', prepareTerms());
};
const handleKeyUpEnter = () => emit('keyUpEnter', prepareTerms());
const prepareTerms = () => inputField.value.trim().split(' ');
const handleKeyUpUp = () => emit('keyUpUp');
const handleKeyUpDown = () => emit('keyUpDown');
const handleReset = () => emit('reset', inputField.value);

const handleChange = () => {
  emit('change', inputField.value);

  if (inputField.value !== '') {
    emit('active', inputField.value);
  } else {
    emit('inactive', inputField.value);
  }
};

const getSearchResults = (searchArray) => {
  const searchTerm = searchArray.join(' ');
  const params = new URLSearchParams(window.location.search);
  if (searchTerm) {
    params.set('search', searchTerm);
  } else {
    params.delete('search');
  }
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);
};

watch(
  () => props.selectedSuggestion,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      inputField.value = newValue;
    }
  },
);

onMounted(() => {
  const params = new URLSearchParams(window.location.search);
  const queryValue = params.get('search') || '';
  let parsedValue = '';
  if (queryValue) {
    parsedValue = queryValue;
  } else if (Array.isArray(props.model) && props.model.length > 0) {
    parsedValue = props.model.join(' ');
  } else if (typeof props.model === 'string') {
    parsedValue = props.model;
  } else {
    parsedValue = '';
  }
  emit('initialize', parsedValue, true);
  inputField.value = parsedValue;
});
</script>

<style module lang="scss">
@forward './Search.scss';
</style>
